/**
 * Pet Feeding Calculator Module
 * 
 * Unified calculation logic for pet daily feeding requirements.
 * Supports both single and mixed (wet/dry) food calculations for cats and dogs.
 * 
 * @module PetFeedingCalculator
 */

class PetFeedingCalculator {
  /**
   * @param {Object} options - Configuration options
   * @param {Object} options.ageGroups - Age groups data by pet type: { dog: [{text, value}], cat: [{text, value}] }
   * @param {Object} options.activityLevels - Activity levels data by pet type: { dog: [{name, value}], cat: [{name, value}] }
   */
  constructor(options = {}) {
    this.ageGroups = options.ageGroups || { dog: [], cat: [] };
    this.activityLevels = options.activityLevels || { dog: [], cat: [] };
  }

  /**
   * Get RER (Resting Energy Requirement) multiplier based on pet category
   * @param {string} category - Pet category: 'cat', 'kitten', 'dog', 'puppy'
   * @returns {number} RER multiplier
   */
  getRERMultiplier(category) {
    if (category === 'cat') {
      return 0.711;
    } else if (category === 'kitten') {
      return 0.67;
    } else if (category === 'dog') {
      return 0.75;
    } else if (category === 'puppy') {
      return 0.75;
    }
    // Default fallback
    return 0.75;
  }

  /**
   * Parse age value from string or number
   * @param {string|number} age - Age value (e.g., "2.5", "1.0", "0-3 months")
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @returns {number} Parsed age value
   */
  parseAgeValue(age, petType) {
    if (!age) return 1.0;

    // Parse age value directly
    const ageNum = parseFloat(age);
    if (!isNaN(ageNum)) {
      return ageNum;
    }

    // Fallback: try to extract number from string
    const numMatch = age.match(/(\d+\.?\d*)/);
    if (numMatch) {
      return parseFloat(numMatch[1]);
    }

    // Default fallback
    return 1.0;
  }

  /**
   * Map quiz activity name to feeding calculator numeric value
   * @param {string} quizActivityValue - Quiz activity value (e.g., "medium_activity", "low activity")
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @returns {string|null} Numeric activity value (e.g., "117.5") or null
   */
  mapQuizActivityToCalculatorValue(quizActivityValue, petType) {
    if (!quizActivityValue) return null;
    
    const valueLower = quizActivityValue.toLowerCase().trim();
    
    // Mapping based on feeding calculator defaults:
    // Dog: Dozer_95.0, Adventurer_117.5, Athlete_165.0
    // Cat: INDOOR_100, OUTDOOR_150
    
    let mappedValue = null;
    if (petType === 'dog') {
      if (valueLower.includes('low') || valueLower.includes('dozer')) {
        mappedValue = '95.0'; // Dozer
      } else if (valueLower.includes('medium') || valueLower.includes('moderate') || valueLower.includes('adventurer')) {
        mappedValue = '117.5'; // Adventurer
      } else if (valueLower.includes('high') || valueLower.includes('athlete')) {
        mappedValue = '165.0'; // Athlete
      }
    } else if (petType === 'cat') {
      // Map cat activity levels: indoor/low = 100, outdoor/high/moderate = 150
      if (valueLower.includes('indoor') || valueLower.includes('low')) {
        mappedValue = '100'; // INDOOR
      } else if (valueLower.includes('outdoor') || valueLower.includes('high') || valueLower.includes('moderate')) {
        mappedValue = '150'; // OUTDOOR - high/moderate activity for cats typically means outdoor
      }
    }
    
    return mappedValue;
  }

  /**
   * Map activity name to numeric value using activity levels data
   * @param {string} activityName - Activity name (e.g., "moderately active")
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @returns {string|null} Numeric activity value or null
   */
  mapActivityNameToValue(activityName, petType) {
    if (!activityName || !this.activityLevels[petType] || this.activityLevels[petType].length === 0) {
      return null;
    }

    const activityNameLower = activityName.toLowerCase().trim();
    
    // Try exact match first
    for (const activityLevel of this.activityLevels[petType]) {
      if (activityLevel.name && activityLevel.name.toLowerCase().trim() === activityNameLower) {
        return this.mapQuizActivityToCalculatorValue(activityLevel.value, petType);
      }
    }
    
    // Try partial match
    for (const activityLevel of this.activityLevels[petType]) {
      if (activityLevel.name) {
        const levelNameLower = activityLevel.name.toLowerCase().trim();
        if (activityNameLower.includes(levelNameLower) || levelNameLower.includes(activityNameLower)) {
          return this.mapQuizActivityToCalculatorValue(activityLevel.value, petType);
        }
      }
    }

    return null;
  }

  /**
   * Parse activity level from various formats
   * @param {string|number} activity - Activity value (e.g., "117.5", "Dozer_95.0", "moderately active")
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @param {Object} [options] - Optional override options
   * @param {string} [options.ageText] - Age text (e.g., "0-3 months") for override logic
   * @param {string} [options.category] - Pet category for override logic
   * @param {number} [options.ageLimit] - Age limit threshold for override (from DOM data attributes)
   * @param {number} [options.kittenPuppyActivity] - Activity override value for kittens/puppies (from DOM data attributes)
   * @returns {number} Parsed activity level (default: 100)
   */
  parseActivityLevel(activity, petType, options = {}) {
    // Puppies/kittens: growth feeding guides ignore activity — use a fixed multiplier.
    // Theme can override via options.kittenPuppyActivity (from data-kitten-puppy-activity).
    const category = options.category;
    if (category === 'puppy' || category === 'kitten') {
      const fixed = parseFloat(options.kittenPuppyActivity);
      if (!isNaN(fixed)) {
        return fixed;
      }
      return 100;
    }

    // Activity format can be:
    // 1. "Text_Value" (e.g., "Dozer_95.0", "INDOOR_100", "OUTDOOR_150") - from feeding calculator
    // 2. Activity name only (e.g., "moderately active") - from quiz metaobjects
    // 3. Numeric value (e.g., "117.5")
    
    // First, try to parse as number directly
    let activityLevel = parseFloat(activity);
    
    // If not a number, try to extract from "Text_Value" format (feeding calculator format)
    if (isNaN(activityLevel) && activity && activity.includes('_')) {
      const parts = activity.split('_');
      if (parts.length > 1) {
        activityLevel = parseFloat(parts[parts.length - 1]);
      }
    }
    
    // If still not a number, try to map quiz activity name to value
    // First try using activityLevels data if available
    if (isNaN(activityLevel) && activity && petType) {
      const mappedValue = this.mapActivityNameToValue(activity, petType);
      if (mappedValue) {
        activityLevel = parseFloat(mappedValue);
      }
    }
    
    // If still not a number and activityLevels not available, try direct mapping
    // This handles cases where activityLevels weren't passed to the module
    if (isNaN(activityLevel) && activity && petType) {
      const directMappedValue = this.mapQuizActivityToCalculatorValue(activity, petType);
      if (directMappedValue) {
        activityLevel = parseFloat(directMappedValue);
      }
    }
    
    // Default fallback if still not a valid number
    if (isNaN(activityLevel)) {
      activityLevel = 100; // Default fallback
    }

    return activityLevel;
  }

  /**
   * Map birthday to age group
   * @param {string|Date} birthday - Pet birthday (ISO string or Date object)
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @returns {Object|null} Age group object with {text, value} or null
   */
  mapBirthdayToAgeGroup(birthday, petType) {
    if (!birthday || !this.ageGroups[petType] || this.ageGroups[petType].length === 0) {
      return null;
    }

    try {
      const birthDate = new Date(birthday);
      const today = new Date();
      const diffTime = Math.abs(today - birthDate);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      // Find matching age group based on text (e.g., "0-3 months", "1 year")
      // Process in order: check months first (for puppies/kittens), then years
      for (const ageGroup of this.ageGroups[petType]) {
        const text = ageGroup.text.toLowerCase();
        
        // Check for month ranges (e.g., "0-3 months", "0-6 months")
        if (text.includes('month')) {
          const monthMatch = text.match(/(\d+)\s*-\s*(\d+)\s*month/i);
          if (monthMatch) {
            const minMonths = parseInt(monthMatch[1], 10);
            const maxMonths = parseInt(monthMatch[2], 10);
            // For very young pets (days old), diffMonths will be 0, so check if 0 is within range
            if (diffMonths >= minMonths && diffMonths <= maxMonths) {
              return { text: ageGroup.text, value: ageGroup.value };
            }
          }
        }
        
        // Check for year ranges (e.g., "1-2 years", "3-6 years")
        if (text.includes('year')) {
          const yearMatch = text.match(/(\d+)\s*-\s*(\d+)\s*year/i);
          if (yearMatch) {
            const minYears = parseInt(yearMatch[1], 10);
            const maxYears = parseInt(yearMatch[2], 10);
            if (diffYears >= minYears && diffYears <= maxYears) {
              return { text: ageGroup.text, value: ageGroup.value };
            }
          }
          const plusMatch = text.match(/(\d+)\s*\+\s*year/i);
          if (plusMatch) {
            const minYears = parseInt(plusMatch[1], 10);
            if (diffYears >= minYears) {
              return { text: ageGroup.text, value: ageGroup.value };
            }
          }
        }
      }

      // Fallback: return null if no match found - let the caller calculate age from birthday directly
      // Don't return first age group as it might be wrong (e.g., "0-4 months" for a 12-year-old)
      return null;
    } catch (error) {
      console.error('Error mapping birthday to age group:', error);
      return null;
    }
  }

  /**
   * Determine pet category (puppy/kitten) based on age
   * @param {string|number} age - Age value or age text
   * @param {string} ageText - Age text (e.g., "0-3 months", "1 year")
   * @param {string} petType - Pet type: 'dog' or 'cat'
   * @param {string} defaultCategory - Default category from product or petType
   * @returns {string} Category: 'cat', 'kitten', 'dog', or 'puppy'
   */
  determineCategory(age, ageText, petType, defaultCategory = null) {
    // Start with default category or petType
    let category = defaultCategory || petType;
    
    // If age text contains "month" or age value < 1 year, it's a puppy/kitten
    const ageTextLower = (ageText || '').toLowerCase();
    const ageValue = typeof age === 'number' ? age : this.parseAgeValue(age, petType);

    // Labels like "3-6 years" or "7+ years" refer to adults — do not treat as puppy/kitten even if
    // the stored select value is a multiplier < 1 (e.g. 0.95).
    const yearSpanMatch = ageTextLower.match(/(\d+)\s*-\s*(\d+)\s*years?/);
    const yearPlusMatch = ageTextLower.match(/(\d+)\s*\+\s*years?/);
    const isAdultYearRangeLabel =
      (yearSpanMatch && parseInt(yearSpanMatch[1], 10) >= 1) ||
      (yearPlusMatch && parseInt(yearPlusMatch[1], 10) >= 1);
    
    if (!isAdultYearRangeLabel && (ageTextLower.includes('month') || (ageValue < 1 && ageTextLower.includes('year')))) {
      if (petType === 'dog') {
        category = 'puppy';
      } else if (petType === 'cat') {
        category = 'kitten';
      }
    } else if (!isAdultYearRangeLabel && ageValue < 1) {
      // e.g. birthday-derived fractional year with no month label in ageText
      if (petType === 'dog') {
        category = 'puppy';
      } else if (petType === 'cat') {
        category = 'kitten';
      }
    }
    
    return category;
  }

  /**
   * Calculate daily grams for single product (wet or dry)
   * @param {Object} params - Calculation parameters
   * @param {number} params.weight - Pet weight in kg
   * @param {string} params.petType - Pet type: 'dog' or 'cat'
   * @param {string} params.age - Age value (e.g., "2.5", "1.0")
   * @param {string} [params.ageText] - Age text (e.g., "0-3 months", "1 year")
   * @param {string|number} params.activity - Activity level (e.g., "117.5", "Dozer_95.0", "moderately active")
   * @param {boolean} [params.treats=false] - Whether pet eats treats
   * @param {number} params.calDensity - Calorie density (kcal per 100g)
   * @param {string} [params.category] - Pet category (auto-determined if not provided)
   * @param {string|Date} [params.birthday] - Pet birthday (for age group mapping)
   * @returns {number} Daily grams (rounded)
   */
  calculateDailyGrams(params) {
    const {
      weight,
      petType,
      age,
      ageText = '',
      activity,
      treats = false,
      calDensity,
      category: providedCategory,
      birthday
    } = params;

    if (!weight || weight <= 0) return 0;
    if (!calDensity || calDensity <= 0) return 0;

    // Determine age value and category
    let ageValue;
    let category = providedCategory;
    
    // CRITICAL FIX: If age is explicitly provided (from dropdown selection), use it instead of recalculating from birthday
    // This matches calculator behavior where manually selected age takes precedence
    if (age && age !== '1.0' && age !== '') {
      // Age was explicitly provided (e.g., from dropdown selection)
      ageValue = this.parseAgeValue(age, petType);
    } else if (birthday && this.ageGroups[petType] && this.ageGroups[petType].length > 0) {
      // Only map birthday to age group if no explicit age was provided
      const ageGroup = this.mapBirthdayToAgeGroup(birthday, petType);
      if (ageGroup) {
        const mappedAgeValue = parseFloat(ageGroup.value);
        const ageGroupText = ageGroup.text.toLowerCase();
        
        // CRITICAL: For puppies/kittens (month ranges), age group values are multipliers (typically > 1.0)
        // For adults (year ranges), age group values are also multipliers (typically around 1.0)
        // We should always use the mapped age group value for puppies/kittens
        const isMonthRange = ageGroupText.includes('month');
        const yearSpanMatch = ageGroupText.match(/(\d+)\s*-\s*(\d+)\s*years?/);
        const yearPlusMatch = ageGroupText.match(/(\d+)\s*\+\s*years?/);
        const isAdultYearRangeLabel =
          (yearSpanMatch && parseInt(yearSpanMatch[1], 10) >= 1) ||
          (yearPlusMatch && parseInt(yearPlusMatch[1], 10) >= 1);
        const isPuppyKitten = isMonthRange || (!isAdultYearRangeLabel && parseFloat(ageGroup.value) < 1 && ageGroupText.includes('year'));
        
        if (isPuppyKitten) {
          // For puppies/kittens, always use the mapped multiplier value
          ageValue = mappedAgeValue;
          if (petType === 'dog') {
            category = 'puppy';
          } else if (petType === 'cat') {
            category = 'kitten';
          }
        } else {
          // For adults, validate mapped age value - if unreasonable, try to find matching age group by years
          const calculatedAgeYears = (() => {
            const birthDate = new Date(birthday);
            const today = new Date();
            const diffTime = Math.abs(today - birthDate);
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            return diffDays / 365.0;
          })();
          
          // If calculated age is >= 1 year, try to find matching age group by years
          if (calculatedAgeYears >= 1) {
            let foundAgeGroup = null;
            const diffYears = Math.floor(calculatedAgeYears);
            for (const ag of this.ageGroups[petType]) {
              const text = ag.text.toLowerCase();
              if (text.includes('year')) {
                const yearMatch = text.match(/(\d+)\s*-\s*(\d+)\s*year/i);
                if (yearMatch) {
                  const minYears = parseInt(yearMatch[1], 10);
                  const maxYears = parseInt(yearMatch[2], 10);
                  if (diffYears >= minYears && diffYears <= maxYears) {
                    foundAgeGroup = ag;
                    break;
                  }
                }
                const plusMatch = text.match(/(\d+)\s*\+\s*year/i);
                if (plusMatch) {
                  const minYears = parseInt(plusMatch[1], 10);
                  if (diffYears >= minYears) {
                    foundAgeGroup = ag;
                    break;
                  }
                }
              }
            }
            ageValue = foundAgeGroup ? parseFloat(foundAgeGroup.value) : 1.0;
          } else {
            // Fallback: use mapped value
            ageValue = mappedAgeValue;
          }
        }
      } else {
        // Fallback: calculate age from birthday
        const birthDate = new Date(birthday);
        const today = new Date();
        const diffTime = Math.abs(today - birthDate);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffMonths = Math.floor(diffDays / 30);
        const calculatedAgeYears = diffDays / 365.0;
        
        // CRITICAL: Age values in the formula are multipliers, not years!
        // For adult pets (> 1 year), we need to find the appropriate age group multiplier
        // If no age group matches, use a default multiplier for adults (typically 1.0)
        if (calculatedAgeYears < 1) {
          // For puppies/kittens, use calculated age as multiplier
          ageValue = calculatedAgeYears;
          if (petType === 'dog') {
            category = 'puppy';
          } else if (petType === 'cat') {
            category = 'kitten';
          }
        } else {
          // For adult pets, try to find matching age group by years, or use default multiplier
          // Look for year ranges in age groups
          let foundAgeGroup = null;
          const diffYears = Math.floor(diffDays / 365);
          for (const ageGroup of this.ageGroups[petType]) {
            const text = ageGroup.text.toLowerCase();
            if (text.includes('year')) {
              const yearMatch = text.match(/(\d+)\s*-\s*(\d+)\s*year/i);
              if (yearMatch) {
                const minYears = parseInt(yearMatch[1], 10);
                const maxYears = parseInt(yearMatch[2], 10);
                if (diffYears >= minYears && diffYears <= maxYears) {
                  foundAgeGroup = ageGroup;
                  break;
                }
              }
              // Also check for "X+ years" format
              const plusMatch = text.match(/(\d+)\s*\+\s*year/i);
              if (plusMatch) {
                const minYears = parseInt(plusMatch[1], 10);
                if (diffYears >= minYears) {
                  foundAgeGroup = ageGroup;
                  break;
                }
              }
            }
          }
          
          if (foundAgeGroup) {
            ageValue = parseFloat(foundAgeGroup.value);
          } else {
            // Default multiplier for adult pets when no age group matches
            // Typically 1.0 for adults, but may vary - using 1.0 as safe default
            ageValue = 1.0;
          }
        }
      }
    } else {
      // Use provided age (fallback)
      ageValue = this.parseAgeValue(age, petType);
    }
    
    // Determine category if not provided (age dropdown values are multipliers, e.g. 3.0 for "0-3 months" — use ageText)
    if (!category) {
      category = this.determineCategory(ageValue, ageText, petType);
    }

    // Get RER multiplier
    const rerMultiplier = this.getRERMultiplier(category);
    
    // Parse activity level (with optional override options from params)
    const activityLevel = this.parseActivityLevel(activity, petType, {
      ageText,
      category,
      ageLimit: params.ageLimit,
      kittenPuppyActivity: params.kittenPuppyActivity
    });

    // Calculate RER (Resting Energy Requirement)
    // Formula: weight^rerMultiplier * age * activityLevel
    let calories = Math.pow(weight, rerMultiplier) * ageValue * activityLevel;

    // Adjust for kittens/puppies (divide by 0.9)
    if (category === 'kitten' || category === 'puppy') {
      calories = calories / 0.9;
    }

    // Calculate grams using direct division
    // Formula: calories / calDensity
    let grams = calories / calDensity;

    // Normalize treats - handle string values: "some", "lots", "no_treats", "yes", "no", boolean true/false
    // "some" and "lots" both reduce by 10%, "no_treats" or false = no reduction
    const treatsValue = typeof treats === 'string' ? treats.toLowerCase().trim() : treats;
    const hasTreats = treats === true || treats === 'true' || treats === 'yes' || 
                      treatsValue === 'some' || treatsValue === 'lots';
    
    if (hasTreats) {
      grams = grams * 0.9;
    }

    // Round to nearest whole number
    return Math.round(grams);
  }

  /**
   * Calculate daily grams for mixed products (wet and dry)
   * @param {Object} params - Calculation parameters
   * @param {number} params.weight - Pet weight in kg
   * @param {string} params.petType - Pet type: 'dog' or 'cat'
   * @param {string} params.age - Age value (e.g., "2.5", "1.0")
   * @param {string} [params.ageText] - Age text (e.g., "0-3 months", "1 year")
   * @param {string|number} params.activity - Activity level
   * @param {number} params.wetRatio - Wet food ratio percentage (0-100)
   * @param {number} params.dryRatio - Dry food ratio percentage (0-100)
   * @param {number} params.wetCalDensity - Wet food calorie density (kcal per 100g)
   * @param {number} params.dryCalDensity - Dry food calorie density (kcal per 100g)
   * @param {string} [params.category] - Pet category (auto-determined if not provided)
   * @param {string|Date} [params.birthday] - Pet birthday (for age group mapping)
   * @returns {Object} Object with {wet: number, dry: number} daily grams (rounded)
   */
  calculateMixedDailyGrams(params) {
    const {
      weight,
      petType,
      age,
      ageText = '',
      activity,
      wetRatio,
      dryRatio,
      wetCalDensity,
      dryCalDensity,
      category: providedCategory,
      birthday
    } = params;

    if (!weight || weight <= 0) return { wet: 0, dry: 0 };
    if (!wetCalDensity || !dryCalDensity) return { wet: 0, dry: 0 };

    // Determine age value and category
    let ageValue;
    let category = providedCategory;
    
    // CRITICAL FIX: If age is explicitly provided (from dropdown selection), use it instead of recalculating from birthday
    // This matches calculator behavior where manually selected age takes precedence
    if (age && age !== '1.0' && age !== '') {
      // Age was explicitly provided (e.g., from dropdown selection)
      ageValue = this.parseAgeValue(age, petType);
    } else if (birthday && this.ageGroups[petType] && this.ageGroups[petType].length > 0) {
      // Only map birthday to age group if no explicit age was provided
      const ageGroup = this.mapBirthdayToAgeGroup(birthday, petType);
      if (ageGroup) {
        const mappedAgeValue = parseFloat(ageGroup.value);
        const ageGroupText = ageGroup.text.toLowerCase();
        
        // CRITICAL: For puppies/kittens (month ranges), age group values are multipliers (typically > 1.0)
        // For adults (year ranges), age group values are also multipliers (typically around 1.0)
        // We should always use the mapped age group value for puppies/kittens
        const isMonthRange = ageGroupText.includes('month');
        const yearSpanMatch = ageGroupText.match(/(\d+)\s*-\s*(\d+)\s*years?/);
        const yearPlusMatch = ageGroupText.match(/(\d+)\s*\+\s*years?/);
        const isAdultYearRangeLabel =
          (yearSpanMatch && parseInt(yearSpanMatch[1], 10) >= 1) ||
          (yearPlusMatch && parseInt(yearPlusMatch[1], 10) >= 1);
        const isPuppyKitten = isMonthRange || (!isAdultYearRangeLabel && parseFloat(ageGroup.value) < 1 && ageGroupText.includes('year'));
        
        if (isPuppyKitten) {
          // For puppies/kittens, always use the mapped multiplier value
          ageValue = mappedAgeValue;
          if (petType === 'dog') {
            category = 'puppy';
          } else if (petType === 'cat') {
            category = 'kitten';
          }
        } else {
          // For adults, validate mapped age value - if unreasonable, try to find matching age group by years
          const calculatedAgeYears = (() => {
            const birthDate = new Date(birthday);
            const today = new Date();
            const diffTime = Math.abs(today - birthDate);
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            return diffDays / 365.0;
          })();
          
          // If calculated age is >= 1 year, try to find matching age group by years
          if (calculatedAgeYears >= 1) {
            let foundAgeGroup = null;
            const diffYears = Math.floor(calculatedAgeYears);
            for (const ag of this.ageGroups[petType]) {
              const text = ag.text.toLowerCase();
              if (text.includes('year')) {
                const yearMatch = text.match(/(\d+)\s*-\s*(\d+)\s*year/i);
                if (yearMatch) {
                  const minYears = parseInt(yearMatch[1], 10);
                  const maxYears = parseInt(yearMatch[2], 10);
                  if (diffYears >= minYears && diffYears <= maxYears) {
                    foundAgeGroup = ag;
                    break;
                  }
                }
                const plusMatch = text.match(/(\d+)\s*\+\s*year/i);
                if (plusMatch) {
                  const minYears = parseInt(plusMatch[1], 10);
                  if (diffYears >= minYears) {
                    foundAgeGroup = ag;
                    break;
                  }
                }
              }
            }
            ageValue = foundAgeGroup ? parseFloat(foundAgeGroup.value) : 1.0;
          } else {
            // Fallback: use mapped value
            ageValue = mappedAgeValue;
          }
        }
      } else {
        // Fallback: calculate age from birthday
        const birthDate = new Date(birthday);
        const today = new Date();
        const diffTime = Math.abs(today - birthDate);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffMonths = Math.floor(diffDays / 30);
        ageValue = diffDays / 365.0;
        
        if (diffMonths < 12) {
          if (petType === 'dog') {
            category = 'puppy';
          } else if (petType === 'cat') {
            category = 'kitten';
          }
        }
      }
    } else {
      // Use provided age (fallback)
      ageValue = this.parseAgeValue(age, petType);
    }
    
    // Determine category if not provided
    if (!category) {
      category = this.determineCategory(ageValue, ageText, petType);
    }

    // Get RER multiplier
    const rerMultiplier = this.getRERMultiplier(category);
    
    // Parse activity level (with optional override options from params)
    const activityLevel = this.parseActivityLevel(activity, petType, {
      ageText,
      category,
      ageLimit: params.ageLimit,
      kittenPuppyActivity: params.kittenPuppyActivity
    });

    // Calculate RER (Resting Energy Requirement)
    // Formula: weight^rerMultiplier * age * activityLevel
    const caloriesBeforeAdjustment = Math.pow(weight, rerMultiplier) * ageValue * activityLevel;
    let calories = caloriesBeforeAdjustment;

    // Adjust for kittens/puppies (divide by 0.9)
    if (category === 'kitten' || category === 'puppy') {
      calories = calories / 0.9;
    }

    // Note: treats adjustment is NOT applied to mixed calculations

    // Calculate mixed feeding based on calorie ratio
    // Convert percentages to decimals (50 -> 0.5)
    const mixWet = wetRatio / 100;
    const mixDry = dryRatio / 100;
    
    // Calculate calories needed from each source based on ratio
    const caloriesFromWet = calories * mixWet;
    const caloriesFromDry = calories * mixDry;
    
    // Calculate grams needed for each food type based on calorie density
    const wetGrams = caloriesFromWet / wetCalDensity;
    const dryGrams = caloriesFromDry / dryCalDensity;

    return {
      wet: Math.round(wetGrams),
      dry: Math.round(dryGrams)
    };
  }
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PetFeedingCalculator;
}

// Export for use in browser (global)
if (typeof window !== 'undefined') {
  window.PetFeedingCalculator = PetFeedingCalculator;
}
